import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogService } from '../services/api';
import { updateMetaTags } from '../utils/seo';
import { Calendar, Tag, ChevronRight, User, ArrowRight, TrendingUp, Sparkles } from 'lucide-react';
import AdSlot from './AdSlot';

const categoryColors = [
  { name: 'Sustainability', bg: 'from-red-500 to-teal-600', light: 'bg-red-50 text-red-700 border-red-200' },
  { name: 'Innovation', bg: 'from-blue-500 to-indigo-600', light: 'bg-blue-50 text-blue-700 border-blue-200' },
  { name: 'Brands', bg: 'from-purple-500 to-pink-600', light: 'bg-purple-50 text-purple-700 border-purple-200' },
  { name: 'Growth', bg: 'from-orange-500 to-red-600', light: 'bg-orange-50 text-orange-700 border-orange-200' },
  { name: 'Technology', bg: 'from-cyan-500 to-blue-600', light: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  { name: 'News', bg: 'from-amber-500 to-orange-600', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  { name: 'Company', bg: 'from-rose-500 to-pink-600', light: 'bg-rose-50 text-rose-700 border-rose-200' },
  { name: 'People', bg: 'from-violet-500 to-purple-600', light: 'bg-violet-50 text-violet-700 border-violet-200' },
];

const getCategoryColor = (tags) => {
  if (!tags || tags.length === 0) return categoryColors[0];
  const firstTag = tags[0].toLowerCase();
  const match = categoryColors.find(c => firstTag.includes(c.name.toLowerCase()));
  return match || categoryColors[0];
};

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    updateMetaTags({
      title: 'News & Insights',
      description: 'Stories, coverage, and company updates from Ortus FMCG Limited.',
      siteName: 'Ortus FMCG'
    });
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const data = await blogService.index({ status: 'published' });
      setBlogs(data.data || data || []);
    } catch (error) {
      console.error('Error loading blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const allCategories = ['All', ...new Set(blogs.flatMap(b => b.tags || []).slice(0, 8))];
  const filteredBlogs = activeCategory === 'All' ? blogs : blogs.filter(b => b.tags?.some(t => t === activeCategory));
  const featuredBlog = filteredBlogs[0];
  const restBlogs = filteredBlogs.slice(1);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-500 font-medium">Loading insights...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Colorful Gradient */}
      <section className="relative bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 text-white py-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
            <Sparkles size={14} className="text-red-400" />
            <p className="text-red-300 text-xs font-bold uppercase tracking-[0.3em]">Media & Updates</p>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
            News & <span className="bg-gradient-to-r from-red-400 to-teal-300 bg-clip-text text-transparent">Insights</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Stay updated with the latest stories, industry trends, and company milestones from the world of Ortus FMCG.
          </p>
        </div>
      </section>

      {/* Top Ad Slot */}
      <div className="max-w-6xl mx-auto px-4 mt-8">
        <AdSlot position="blog_top" className="h-[180px]" />
      </div>

      {/* Category Filter Pills */}
      {allCategories.length > 1 && (
        <div className="max-w-6xl mx-auto px-4 mt-12">
          <div className="flex flex-wrap gap-2 justify-center">
            {allCategories.map((cat) => {
              const color = getCategoryColor([cat]);
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2.5 rounded-full font-bold text-xs uppercase tracking-wider transition-all duration-300 ${
                    activeCategory === cat
                      ? 'bg-gradient-to-r ' + color.bg + ' text-white shadow-lg scale-105'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-16">
        {/* Featured Article Hero - HUL Style */}
        {featuredBlog && (
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp size={20} className="text-red-600" />
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">Latest News</h2>
              <div className="h-1 flex-1 bg-gradient-to-r from-red-200 to-transparent ml-4 rounded-full"></div>
            </div>
            <Link to={`/blog/${featuredBlog.slug}`} className="group block relative rounded-3xl overflow-hidden shadow-2xl">
              {/* Background Image */}
              <div className="aspect-[21/9] relative">
                {featuredBlog.featured_image ? (
                  <img 
                    src={featuredBlog.featured_image} 
                    alt={featuredBlog.title} 
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-slate-900"></div>
                )}
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
              </div>
              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <div className="flex items-center gap-3 mb-4">
                  {featuredBlog.is_featured && (
                    <span className="bg-red-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <Sparkles size={10} /> Featured
                    </span>
                  )}
                  {featuredBlog.tags?.slice(0, 1).map(tag => {
                    const color = getCategoryColor([tag]);
                    return (
                      <span key={tag} className={`bg-gradient-to-r ${color.bg} text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg`}>
                        {tag}
                      </span>
                    );
                  })}
                </div>
                <h2 className="text-2xl md:text-4xl font-black text-white mb-4 leading-tight group-hover:text-red-300 transition-colors max-w-3xl">
                  {featuredBlog.title}
                </h2>
                <p className="text-slate-300 text-sm md:text-base max-w-2xl line-clamp-2 mb-4">
                  {featuredBlog.excerpt || featuredBlog.content?.replace(/<[^>]*>/g, '').substring(0, 200) + '...'}
                </p>
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={14} className="text-red-400" />
                    {new Date(featuredBlog.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  {featuredBlog.author && (
                    <div className="flex items-center gap-1.5">
                      <User size={14} className="text-red-400" />
                      {featuredBlog.author.name}
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-red-400 font-bold uppercase tracking-widest group-hover:gap-2 transition-all">
                    Read Article <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* More Articles Grid */}
        {restBlogs.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-10">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">More Articles</h3>
              <div className="h-1 flex-1 bg-slate-100 mx-6 hidden md:block rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {restBlogs.map((blog) => {
                const color = getCategoryColor(blog.tags);
                return (
                  <Link to={`/blog/${blog.slug}`} key={blog.id} className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col">
                    {/* Image Header with Gradient Overlay */}
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      {blog.featured_image ? (
                        <img 
                          src={blog.featured_image} 
                          alt={blog.title} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${color.bg} flex items-center justify-center`}>
                          <Tag size={48} className="text-white/30" />
                        </div>
                      )}
                      {/* Category Badge */}
                      {blog.tags && blog.tags[0] && (
                        <div className="absolute top-4 left-4">
                          <span className={`bg-gradient-to-r ${color.bg} text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg`}>
                            {blog.tags[0]}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={14} className="text-red-500" />
                          {new Date(blog.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        {blog.author && (
                          <div className="flex items-center gap-1.5">
                            <User size={14} className="text-red-500" />
                            {blog.author.name}
                          </div>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2 leading-snug">
                        {blog.title}
                      </h3>
                      
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 line-clamp-3">
                        {blog.excerpt || blog.content?.replace(/<[^>]*>/g, '').substring(0, 150) + '...'}
                      </p>

                      <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                          {blog.tags && blog.tags.slice(1, 3).map(tag => (
                            <span key={tag} className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-1 rounded ${getCategoryColor([tag]).light}`}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-1 text-red-600 text-xs font-black uppercase tracking-widest group-hover:gap-2 transition-all">
                          Read More <ChevronRight size={14} />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {blogs.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
              <Tag size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Articles Found</h3>
            <p className="text-slate-500 max-w-sm mx-auto leading-relaxed">
              We haven't published any articles yet. Please check back later for updates and insights.
            </p>
          </div>
        )}

        {/* Bottom Ad Slot */}
        <div className="mt-16">
          <AdSlot position="blog_bottom" className="h-[250px]" />
        </div>
      </main>
    </div>
  );
}

export default Blog;
